/* eslint-disable @typescript-eslint/no-explicit-any */
import { getNodeType, objectIncludes, type Command } from "@tiptap/core"
import type { NodeType } from "@tiptap/pm/model"
import type { EditorState } from "@tiptap/pm/state"
import { isFlatListType } from "tiptap-extension-flat-list"

// Helpers for working with "normal" block nodes (paragraph, heading, list item),
// specifically, text blocks in group "block".

/**
 * Checks if all "normal" block nodes in the selection have the given block type.
 *
 * This is similar to Tiptap's isNodeActive, but it skips titles and widgets.
 */
export function isBlockTypeActive(
  state: EditorState,
  typeOrName: NodeType | string,
  attributes: Record<string, any> = {},
) {
  const { from, to, empty } = state.selection
  const type = getNodeType(typeOrName, state.schema)

  let hasMatch = false
  let hasNonMatch = false

  state.doc.nodesBetween(from, to, (node) => {
    if (node.isTextblock && node.type.isInGroup("block")) {
      if (node.type === type && objectIncludes(node.attrs, attributes, { strict: false })) {
        hasMatch = true
      } else hasNonMatch = true
    }
  })

  return empty ? hasMatch : hasMatch && !hasNonMatch
}

/**
 * Command that toggles the block type.
 *
 * Same as Tiptap's toggleNode except:
 * - It uses isBlockTypeActive instead of isNodeActive.
 * - We incorporate tiptap-extension-flat-list's custom toggleFlatListItem.
 */
export function toggleBlockType(
  typeOrName: string | NodeType,
  toggleTypeOrName: string | NodeType,
  attributes?: Record<string, any>,
): Command {
  return ({ state, commands }) => {
    const type = getNodeType(typeOrName, state.schema)
    const toggleType = getNodeType(toggleTypeOrName, state.schema)
    // Cp mod: isNodeActive -> isBlockTypeActive
    const isActive = isBlockTypeActive(state, type, attributes)

    let attributesToCopy: Record<string, any> | undefined

    if (state.selection.$anchor.sameParent(state.selection.$head)) {
      // only copy attributes if the selection is pointing to a node of the same type
      attributesToCopy = state.selection.$anchor.parent.attrs
    }

    if (isActive) {
      return commands.setNode(toggleType, attributesToCopy)
    }

    // If the node is not active, we want to set the new node type with the given attributes
    // Copying over the attributes from the current node if the selection is pointing to a node of the same type

    // Cp mod: If type is from tiptap-extension-flat-list, call setFlatListItem instead of setNode.
    // It preserves indents when changing list types.
    if (isFlatListType(type.name)) {
      return commands.setFlatListItem(type.name, {
        ...attributesToCopy,
        ...attributes,
      })
    } else {
      return commands.setNode(type, { ...attributesToCopy, ...attributes })
    }
  }
}

/**
 * Command that sets the block type.
 *
 * Same as Tiptap's setNode except:
 * - We incorporate tiptap-extension-flat-list's custom setFlatListItem,
 * which preserves indents when changing between list types.
 */
export function setBlockType(
  typeOrName: string | NodeType,
  attributes?: Record<string, any>,
): Command {
  return ({ state, commands }) => {
    const type = getNodeType(typeOrName, state.schema)
    // Cp mod: If type is from tiptap-extension-flat-list, call setFlatListItem instead of setNode.
    // It preserves indents when changing list types.
    if (isFlatListType(type.name)) {
      return commands.setFlatListItem(type.name, attributes)
    } else {
      return commands.setNode(type, attributes)
    }
  }
}
